import { Box, Divider, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { dashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import { LATEST, TimePoint } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import {
  baseTimePointOptions,
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  createTrendSortMenuItems,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import WidgetWrapper from "../../WidgetWrapper";
import MetricCard from "./MetricCard";

const OpenGapsAgeWidget = forwardRef(({ mainTimePoint }, ref) => {
  const colors = useColors();
  const { getOpenGapsAgeTrend, trackEvent } = useAPI();
  const widgetContentRef = useRef(null);
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);

  const { data: openGapsAgeTrend, isLoading: isLoadingOpenGapsAgeTrend } =
    useQuery({
      queryKey: dashboardKeys.widgetType("openGapsAgeTrend"),
      queryFn: getOpenGapsAgeTrend,
      select: (data) => data.data,
    });

  useEffect(() => {
    if (openGapsAgeTrend && openGapsAgeTrend[mainTimePoint]) {
      setBaseTimePoint(mainTimePoint);
    }
  }, [mainTimePoint, openGapsAgeTrend]);

  const sortMenuItems = createTrendSortMenuItems(
    baseTimePoint,
    setBaseTimePoint,
    openGapsAgeTrend,
    icons,
    trackEvent,
    "open-gaps-age"
  );

  const { overallStats, highSeverityStats, trendData } = useMemo(() => {
    const current = openGapsAgeTrend?.[LATEST] || {};
    const base =
      baseTimePoint === LATEST
        ? current
        : openGapsAgeTrend?.[baseTimePoint] || {};

    const overall = current?.overall || {};
    const highSeverity = current?.high_severity || {};
    const baseOverall = base?.overall || {};
    const baseHighSeverity = base?.high_severity || {};

    const calculateTrend = (currentVal, baseVal) => {
      const diff = currentVal - baseVal;
      const percentChange =
        baseVal !== 0
          ? Math.round((diff / baseVal) * 100)
          : currentVal > 0
          ? 100
          : 0;
      return {
        diff,
        percentChange: Math.abs(percentChange),
        isPositive: diff < 0,
      };
    };

    const trends =
      baseTimePoint === LATEST
        ? null
        : {
            overall: {
              average: calculateTrend(
                overall.average_duration_days || 0,
                baseOverall.average_duration_days || 0
              ),
              longest: calculateTrend(
                overall.longest_gap_days || 0,
                baseOverall.longest_gap_days || 0
              ),
            },
            highSeverity: {
              average: calculateTrend(
                highSeverity.average_duration_days || 0,
                baseHighSeverity.average_duration_days || 0
              ),
              longest: calculateTrend(
                highSeverity.longest_gap_days || 0,
                baseHighSeverity.longest_gap_days || 0
              ),
            },
          };

    return {
      overallStats: overall,
      highSeverityStats: highSeverity,
      trendData: trends,
    };
  }, [openGapsAgeTrend, baseTimePoint]);

  const handleViewGap = useCallback(
    (controlId) => {
      trackEvent(
        `User clicked View Gap in Open Gaps Age widget - Control ${controlId}`
      );
      window.open(`/posture/controls?control_id=${controlId}`, "_blank");
    },
    [trackEvent]
  );

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!widgetContentRef.current || isLoadingOpenGapsAgeTrend) return null;

      if (!isCombinedExport) {
        trackEvent("User clicked on Export to PDF button in Open Gaps Age");
      }

      try {
        if (isCombinedExport) {
          const widgetContainer = document.createElement("div");

          const widgetHeader = createPdfHeader("Open Gaps Age Report");
          widgetContainer.appendChild(widgetHeader);

          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
            background-color: white;
            padding: 24px;
            border: none;
            width: 89%;
            min-height: fit-content;
          `;

          widgetContainer.appendChild(widgetClone);

          return widgetContainer;
        } else {
          const entirePageElement = await createPdfContainer();

          const header = createPdfHeader("Open Gaps Age Report");
          entirePageElement.appendChild(header);

          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
            background-color: white;
            padding: 24px;
            border: none;
            width: 89%;
            min-height: fit-content;
          `;

          entirePageElement.appendChild(widgetClone);

          const { contentWidth, contentHeight } = await measureContentForPdf(
            entirePageElement
          );

          const pdfConfig = createPdfConfig(
            generateFileName("Breeze_Open_Gaps_Age", null, FILE_EXTENSIONS.PDF),
            contentWidth,
            contentHeight
          );

          await html2pdf().set(pdfConfig).from(entirePageElement).save();
        }
      } catch (error) {
        Toast.error("Failed to export Open Gaps Age, Please try again later");
        return null;
      }
    },
    [widgetContentRef, isLoadingOpenGapsAgeTrend, trackEvent]
  );

  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "openGapsAge",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );

  return (
    <WidgetWrapper
      title="Open Gaps Age"
      sortMenuItems={{
        data: sortMenuItems,
        tooltip: "View changes since selected time",
      }}
      info="Displays how long gaps remain open across your environment, showing both average and longest durations for overall and high-severity gaps."
      handleExportToPdf={() => handleExportToPdf(false)}
    >
      {isLoadingOpenGapsAgeTrend ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <LoadingIcon size={24} />
        </Box>
      ) : (
        <Box ref={widgetContentRef} sx={{ padding: "8px 16px 16px 16px" }}>
          <Typography
            variant="caption"
            sx={{
              color: colors.text_secondary,
              marginBottom: 2,
              display: "block",
              padding: "0px 18px",
            }}
          >
            Current status{" "}
            {baseTimePoint !== LATEST
              ? `vs. ${baseTimePointOptions[baseTimePoint]}`
              : ""}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexDirection: "row",
              gap: 2,
              padding: "0px 18px",
            }}
          >
            <MetricCard
              label="Average"
              value={overallStats.average_duration_days || 0}
              unit="days"
              colors={colors}
              trend={trendData?.overall?.average}
              sx={{ flex: 1 }}
            />
            <Divider orientation="vertical" flexItem />
            <MetricCard
              label="Average - High Severity"
              value={highSeverityStats.average_duration_days || 0}
              unit="days"
              colors={colors}
              trend={trendData?.highSeverity?.average}
              sx={{ flex: 1 }}
            />
            <Divider orientation="vertical" flexItem />
            <MetricCard
              label="Longest"
              value={overallStats.longest_gap_days || 0}
              unit="days"
              showViewGap
              controlId={overallStats.longest_gap_control_id}
              onViewGap={handleViewGap}
              colors={colors}
              trend={trendData?.overall?.longest}
              sx={{ flex: 1 }}
            />
            <Divider orientation="vertical" flexItem />
            <MetricCard
              label="Longest - High Severity"
              value={highSeverityStats.longest_gap_days || 0}
              unit="days"
              showViewGap
              controlId={highSeverityStats.longest_gap_control_id}
              onViewGap={handleViewGap}
              colors={colors}
              trend={trendData?.highSeverity?.longest}
              sx={{ flex: 1 }}
            />
          </Box>
        </Box>
      )}
    </WidgetWrapper>
  );
});

OpenGapsAgeWidget.displayName = "OpenGapsAgeWidget";

export default OpenGapsAgeWidget;
